import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { User } from "../types";
import { useAuth } from "@/contexts/AuthContext";
import { useRolePermissions } from "@/hooks/useRolePermissions";
import { assignAdminRole } from "@/utils/userSettingsUtils";

export const useUserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isServiceRoleAvailable, setIsServiceRoleAvailable] = useState(false);
  const { user: currentUser } = useAuth();
  const { isSuper, canManageAllUsers } = useRolePermissions();
  
  // Filter that can be used for showing all users or just team members
  const [showAllUsers, setShowAllUsers] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Check service role access (in both production and development)
      let hasServiceAccess = false;
      
      try {
        const { data, error } = await supabase.auth.admin.listUsers({ page: 1, perPage: 1 });
        if (!error && data) {
          hasServiceAccess = true;
        }
      } catch (error) {
        console.log("No service role access available");
      }
      
      if (hasServiceAccess) {
        setIsServiceRoleAvailable(true);
        const { data, error } = await supabase.auth.admin.listUsers();

        if (error) throw error;

        // Fetch user roles from user_roles table
        const { data: rolesData, error: rolesError } = await supabase
          .from('user_roles')
          .select('user_id, role, is_active, subscription_status, plan_tier, created_by, team_id');
          
        if (rolesError) {
          console.error("Error fetching user roles:", rolesError);
        }
        
        // Create a map of user IDs to their roles and other data
        const userRolesMap = new Map();
        if (rolesData) {
          rolesData.forEach(item => {
            userRolesMap.set(item.user_id, {
              role: item.role,
              is_active: item.is_active,
              subscription_status: item.subscription_status,
              plan_tier: item.plan_tier,
              created_by: item.created_by,
              team_id: item.team_id
            });
          });
        }

        if (data?.users) {
          const allUsers = data.users.map(user => ({
            id: user.id,
            email: user.email || 'No Email',
            created_at: new Date(user.created_at || '').toLocaleDateString(),
            role: userRolesMap.get(user.id)?.role || 'member',
            is_active: userRolesMap.get(user.id)?.is_active || true,
            subscription_status: userRolesMap.get(user.id)?.subscription_status || 'free',
            plan_tier: userRolesMap.get(user.id)?.plan_tier || 'basic',
            created_by: userRolesMap.get(user.id)?.created_by || null,
            team_id: userRolesMap.get(user.id)?.team_id || null
          }));
          
          // Apply filtering based on permissions and filter settings
          let filteredUsers = allUsers;
          
          // If not super or not showing all users, only show users created by current user
          if (!canManageAllUsers && !showAllUsers && currentUser) {
            filteredUsers = allUsers.filter(user => 
              user.created_by === currentUser.id || user.id === currentUser.id
            );
          }
          
          setUsers(filteredUsers);
        }
      } else {
        console.log("Service role not available, using fallback data");
        setIsServiceRoleAvailable(false);
        
        // Try to get user emails from user_roles joined with auth
        const { data: userRolesData, error: userRolesError } = await supabase
          .from('user_roles')
          .select('*');
          
        if (userRolesError) {
          console.error("Error fetching user roles:", userRolesError);
          // Use sample data for development
          setUsers([
            { id: "1", email: "admin@example.com", created_at: new Date().toLocaleDateString(), role: "admin" },
            { id: "2", email: "user@example.com", created_at: new Date().toLocaleDateString(), role: "user" }
          ]);
          
          toast({
            title: "מצב פיתוח בלבד",
            description: "משתמשי דוגמה מוצגים. להפעלה מלאה, הגדר תפקיד שירות בסופאבייס"
          });
          
          return;
        }
        
        // Apply filtering based on permissions
        let filteredRolesData = userRolesData;
        if (!canManageAllUsers && !showAllUsers && currentUser) {
          filteredRolesData = userRolesData.filter(role => 
            role.created_by === currentUser.id || role.user_id === currentUser.id
          );
        }
        
        // Since we can't access the auth.users table directly, 
        // we'll use the user_id from user_roles and display what we have
        if (filteredRolesData && filteredRolesData.length > 0) {
          // Also try to fetch any email data we might have stored
          const { data: userSettingsData, error: userSettingsError } = await supabase
            .from('user_settings')
            .select('user_id, settings_value')
            .filter('settings_key', 'eq', 'user_profile');
            
          // Create a map of user IDs to email addresses from settings if available
          const userEmailMap = new Map();
          if (!userSettingsError && userSettingsData) {
            userSettingsData.forEach(setting => {
              if (setting.settings_value && typeof setting.settings_value === 'object' && 'email' in setting.settings_value) {
                userEmailMap.set(setting.user_id, setting.settings_value.email);
              }
            });
          }
          
          setUsers(filteredRolesData.map(userRole => ({
            id: userRole.user_id,
            // Use email from user settings if available, otherwise use a placeholder
            email: userEmailMap.get(userRole.user_id) || `user-${userRole.user_id.substring(0, 8)}@example.com`,
            created_at: new Date(userRole.created_at).toLocaleDateString(),
            role: userRole.role,
            is_active: userRole.is_active,
            subscription_status: userRole.subscription_status || 'free',
            plan_tier: userRole.plan_tier || 'basic',
            created_by: userRole.created_by,
            team_id: userRole.team_id
          })));
        } else {
          // Use sample data for development
          setUsers([
            { id: "1", email: "admin@example.com", created_at: new Date().toLocaleDateString(), role: "admin" },
            { id: "2", email: "user@example.com", created_at: new Date().toLocaleDateString(), role: "user" }
          ]);
          
          toast({
            title: "מצב פיתוח בלבד",
            description: "משתמשי דוגמה מוצגים. להפעלה מלאה, הגדר תפקיד שירות בסופאבייס"
          });
        }
      }
    } catch (error: any) {
      console.error("Error fetching users:", error);
      setIsServiceRoleAvailable(false);
      
      toast({
        variant: "destructive",
        title: "שגיאה בטעינת משתמשים",
        description: "אין הרשאות מתאימות לגישה למשתמשים. נדרשות הרשאות מנהל."
      });
      
      // Use sample users in development
      setUsers([
        { id: "1", email: "admin@example.com", created_at: new Date().toLocaleDateString(), role: "admin" },
        { id: "2", email: "user@example.com", created_at: new Date().toLocaleDateString(), role: "user" }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserEmail || !newUserPassword) {
      toast({
        variant: "destructive",
        title: "שגיאה",
        description: "נא למלא דוא״ל וסיסמה"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      if (isServiceRoleAvailable) {
        const { data, error } = await supabase.auth.admin.createUser({
          email: newUserEmail,
          password: newUserPassword,
          email_confirm: true
        });

        if (error) throw error;

        if (data?.user) {
          // Add user role record with current user as created_by
          const { error: roleError } = await supabase
            .from('user_roles')
            .insert({
              user_id: data.user.id,
              role: 'viewer', // Default role for invited users
              is_active: true,
              subscription_status: 'free',
              plan_tier: 'basic',
              created_by: currentUser?.id,
              team_id: null // Can be set if implementing team functionality
            });
            
          if (roleError) {
            console.error("Error creating user role:", roleError);
          }
          
          toast({
            title: "משתמש נוצר בהצלחה",
            description: `המשתמש ${newUserEmail} נוצר בהצלחה`
          });
          
          // Add new user to the list
          setUsers(prev => [
            ...prev, 
            {
              id: data.user.id,
              email: data.user.email || newUserEmail,
              created_at: new Date(data.user.created_at || '').toLocaleDateString(),
              role: 'viewer', // Default role for invited users
              is_active: true,
              subscription_status: 'free',
              plan_tier: 'basic',
              created_by: currentUser?.id,
              team_id: null
            }
          ]);
        }
      } else {
        // Fallback for development or limited access
        const { data, error } = await supabase.auth.signUp({
          email: newUserEmail,
          password: newUserPassword,
        });
        
        if (error) throw error;
        
        if (data?.user) {
          toast({
            title: "משתמש נרשם בהצלחה",
            description: "הזמנה לאימות דוא\"ל נשלחה (במצב פיתוח)"
          });
          
          const newUser = {
            id: data.user.id,
            email: data.user.email || newUserEmail,
            created_at: new Date().toLocaleDateString(),
            role: 'viewer',
            is_active: true,
            subscription_status: 'free',
            plan_tier: 'basic',
            created_by: currentUser?.id,
            team_id: null
          };
          
          setUsers(prev => [newUser, ...prev]);
        }
      }
      
      // Close dialog and reset form
      setIsAddDialogOpen(false);
      setNewUserEmail("");
      setNewUserPassword("");
    } catch (error: any) {
      console.error("Error creating user:", error);
      toast({
        variant: "destructive",
        title: "שגיאה ביצירת משתמש",
        description: error.message || "אירעה שגיאה בעת יצירת משתמש חדש"
      });
      
      // For development - if we can't access admin endpoints
      if (error.message?.includes('not authorized') || error.message?.includes('not allowed')) {
        toast({
          variant: "destructive", 
          title: "הודעת פיתוח",
          description: "משתמש יתווסף רק לטבלה המקומית בסביבת הפיתוח"
        });
        
        // Add dummy user in development
        const newUser = {
          id: Math.random().toString(36).substring(2, 9),
          email: newUserEmail,
          created_at: new Date().toLocaleDateString(),
          role: 'viewer',
          is_active: true,
          subscription_status: 'free',
          plan_tier: 'basic',
          created_by: currentUser?.id,
          team_id: null
        };
        
        setUsers(prev => [...prev, newUser]);
        setIsAddDialogOpen(false);
        setNewUserEmail("");
        setNewUserPassword("");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      if (isServiceRoleAvailable) {
        const { error } = await supabase.auth.admin.deleteUser(userId);

        if (error) throw error;

        toast({
          title: "משתמש נמחק בהצלחה",
          description: `המשתמש נמחק בהצלחה`
        });
      } else {
        toast({
          variant: "destructive",
          title: "אין הרשאות מחיקה",
          description: "פעולה זו זמינה רק למנהל מערכת"
        });
      }
      
      // Update UI regardless of backend success (for development experience)
      setUsers(users.filter(user => user.id !== userId));
    } catch (error: any) {
      console.error("Error deleting user:", error);
      
      // Still update UI for better development experience
      setUsers(users.filter(user => user.id !== userId));
      
      toast({
        variant: "destructive",
        title: "שגיאה במחיקת משתמש",
        description: error.message || "אירעה שגיאה בעת מחיקת המשתמש"
      });
    }
  };

  const handleToggleAdminRole = async (userId: string, isCurrentlyAdmin: boolean) => {
    try {
      const newRole = isCurrentlyAdmin ? 'member' : 'admin';
      
      // Update the role in the database
      const { error } = await supabase
        .from('user_roles')
        .update({ role: newRole })
        .eq('user_id', userId);
        
      if (error) throw error;
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId 
          ? { 
              ...user, 
              role: newRole
            } 
          : user
      ));
      
      toast({
        title: "תפקיד משתמש עודכן",
        description: `המשתמש עודכן לתפקיד ${newRole}`
      });
    } catch (error: any) {
      console.error("Error changing user role:", error);
      toast({
        variant: "destructive",
        title: "שגיאה בשינוי תפקיד",
        description: error.message || "אירעה שגיאה בעת שינוי תפקיד המשתמש"
      });
    }
  };
  
  // Function to toggle user active status
  const handleToggleUserActive = async (userId: string, currentlyActive: boolean) => {
    try {
      // Update user_roles table
      const { error } = await supabase
        .from('user_roles')
        .update({ is_active: !currentlyActive })
        .eq('user_id', userId);
        
      if (error) throw error;
      
      setUsers(users.map(user => 
        user.id === userId 
          ? { ...user, is_active: !currentlyActive } 
          : user
      ));
      
      toast({
        title: currentlyActive ? "משתמש הושבת" : "משתמש הופעל",
        description: `סטטוס המשתמש עודכן בהצלחה`
      });
    } catch (error: any) {
      console.error("Error toggling user active status:", error);
      toast({
        variant: "destructive",
        title: "שגיאה בעדכון סטטוס משתמש",
        description: error.message || "אירעה שגיאה בעת עדכון סטטוס המשתמש"
      });
    }
  };
  
  // Function to change a user's role
  const handleChangeUserRole = async (userId: string, newRole: string) => {
    try {
      // Super users can change any role except their own to super
      if (newRole === 'super' && !isSuper) {
        toast({
          variant: "destructive",
          title: "פעולה לא מורשית",
          description: "רק משתמש-על יכול לקבוע משתמש-על אחר"
        });
        return;
      }
      
      // Don't allow changing own role
      if (userId === currentUser?.id) {
        toast({
          variant: "destructive",
          title: "פעולה לא מורשית",
          description: "לא ניתן לשנות את התפקיד שלך"
        });
        return;
      }
      
      // Update the role in the database
      const { error } = await supabase
        .from('user_roles')
        .update({ role: newRole })
        .eq('user_id', userId);
        
      if (error) throw error;
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId 
          ? { 
              ...user, 
              role: newRole
            } 
          : user
      ));
      
      toast({
        title: "תפקיד משתמש עודכן",
        description: `המשתמש עודכן לתפקיד ${newRole}`
      });
    } catch (error: any) {
      console.error("Error changing user role:", error);
      toast({
        variant: "destructive",
        title: "שגיאה בשינוי תפקיד",
        description: error.message || "אירעה שגיאה בעת שינוי תפקיד המשתמש"
      });
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [showAllUsers, currentUser]);

  return {
    users,
    loading,
    isAddDialogOpen,
    setIsAddDialogOpen,
    newUserEmail,
    setNewUserEmail,
    newUserPassword,
    setNewUserPassword,
    isSubmitting,
    isServiceRoleAvailable,
    showAllUsers,
    setShowAllUsers,
    fetchUsers,
    handleAddUser,
    handleDeleteUser,
    handleToggleAdminRole,
    handleToggleUserActive,
    handleChangeUserRole
  };
};
